"use client";

import { Button } from "@/components/ui/button";
import Link from "next/link";
import { cn } from "@/lib/utils";
import parse from 'html-react-parser';
import { useEffect } from 'react';

export interface ThankYouPageConfig {
  title: string;
  subtitle: string;
  firstName?: string;
  businessName?: string;
  nextStepsTitle: string;
  nextStepsItems: Array<{
    icon: string;
    text: string;
    boldText?: string;
  }>;
  ctaButton: {
    text: string;
    href: string;
  };
  additionalInfo: string;
  socialButton: {
    text: string;
    href: string;
  };
  backButtonLink?: string;
}

interface ThankYouPageProps {
  config: ThankYouPageConfig;
  className?: string;
  brandColor?: string;
  brandColorHover?: string;
  brandColorText?: string;
  brandColorLink?: string;
  brandColorLinkHover?: string;
}

export const ThankYouPage = ({ 
  config, 
  className, 
  brandColor, 
  brandColorHover, 
  brandColorText, 
  brandColorLink, 
  brandColorLinkHover 
}: ThankYouPageProps) => {
  const {
    title,
    subtitle,
    firstName,
    businessName,
    nextStepsTitle,
    nextStepsItems,
    ctaButton,
    additionalInfo,
    socialButton,
    backButtonLink
  } = config;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      {/* Brand Color Styles */}
      {brandColor && brandColorHover && brandColorText && (
        <style dangerouslySetInnerHTML={{
          __html: `
            .button-branded {
              background-color: ${brandColor} !important;
              color: ${brandColorText} !important;
            }

            .button-branded:hover {
              background-color: ${brandColorHover} !important;
            }
          `
        }} />
      )}
      {brandColorLink && brandColorLinkHover && (
        <style dangerouslySetInnerHTML={{
          __html: `
            .text-brand {
              color: ${brandColorLink} !important;
            }
            p a {
              color: ${brandColorLink} !important;
            }
            p a:hover {
              color: ${brandColorLinkHover} !important;
            }
          `
        }} />
      )}
      <div className={cn("w-full max-w-2xl mx-auto md:px-4", className)}>
        {/* Back Button */}
        <div className="flex flex-col gap-4 mb-8">
          <Button variant="outline" asChild grayBorder size="iconLg">
            <Link href={backButtonLink || "/enquire"}>
              <span className="material-symbols-outlined">
                arrow_back
              </span>
            </Link>
          </Button>
        </div>

        {/* Main Content */}
        <div className="flex flex-col gap-12">
          {/* Header Section */}
          <div className="flex flex-col gap-4">
            <h3 className="!text-wrap">
              {title}
              {firstName && (
                <span className="text-foreground font-bold">, {firstName}</span>
              )}
              !
            </h3>
            <p className="paragraph-lg text-foreground leading-relaxed">
              {parse(subtitle)}
            </p>
          </div>

          {/* What's Next Section */}
          <div className="flex flex-col gap-6">
            {/* Separator with title */}
            <div className="flex items-center gap-4">
              <span className="paragraph-base font-semibold text-foreground">
                {nextStepsTitle}
                {businessName && (
                  <span className="text-brand paragraph-base"> {businessName}</span>
                )}
              </span>
            </div>

            {/* Information Card */}
            <div className="border border-border rounded-3xl">
              {nextStepsItems.map((item, index) => (
                <div 
                  key={index}
                  className={cn(
                    "w-full px-6 py-4 flex items-center gap-6 ",
                    index === 0 && "rounded-t-3xl",
                    index === nextStepsItems.length - 1 && "rounded-b-3xl",
                    index < nextStepsItems.length - 1 && "border-b border-border"
                  )}
                >
                  <span className="material-symbols-outlined text-foreground">
                    {item.icon}
                  </span>
                  <p className="paragraph-base text-muted-foreground [&_strong]:text-foreground">
                    {parse(item.text)}
                  </p>
                </div>
              ))}
            </div>
          
          </div>

          {/* Call to Action Button */}
          <div className="flex flex-col gap-4">
            <Button asChild size="lg" variant="primary" className="button-branded">
              <Link href={ctaButton.href}>
                {ctaButton.text}
              </Link>
            </Button>
          </div>

          {/* Bottom Section */}
          <div className="flex flex-col gap-4">
            <p className="paragraph-base text-foreground leading-relaxed text-center">
              {additionalInfo}
            </p>
            
            <Button asChild variant="outline" size="lg" className="border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white">
              <Link href={socialButton.href}>
                {socialButton.text}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
